import { Temporal } from 'temporal-polyfill';
import { isoDateText, parseDateUnit, today, toPlainDate } from './dates';
import {
  canonicalKpiPeriod,
  canonicalKpiSection,
  KPI_PERIOD_KEYS,
  KPI_SECTION_KEYS,
  kpiSectionLabel,
} from './kpi';
import { CallArg, CheckCallContext, createRegistry, EvalCallContext, FunctionSpec } from './registry';
import { AstNode, FormulaError, FormulaTypeError, isFormulaError } from './types';
import {
  BLANK,
  compareValues,
  EvalValue,
  finiteOrNumError,
  parseNumber,
  requireBoolean,
  requireNumber,
  round,
  toText,
} from './values';

/** The evaluated values of a plain function's expr arguments, in order. */
function values(args: CallArg[]): EvalValue[] {
  return args.map((arg) => (arg.kind === 'value' ? arg.value : BLANK));
}

/** Every expr argument as a number, or the first #VALUE! for one that is text. */
function numbers(args: CallArg[]): number[] | FormulaError {
  const out: number[] = [];
  for (const value of values(args)) {
    const number = requireNumber(value);
    if (isFormulaError(number)) {
      return number;
    }
    out.push(number);
  }
  return out;
}

function numeric(
  spec: Omit<FunctionSpec, 'evaluate'>,
  apply: (numbers: number[]) => EvalValue | FormulaError
): FunctionSpec {
  return {
    ...spec,
    evaluate: (args) => {
      const parsed = numbers(args);
      return isFormulaError(parsed) ? parsed : apply(parsed);
    },
  };
}

/** Every expr argument as TRUE/FALSE, or the first #VALUE! for one that is not. */
function booleans(args: CallArg[]): boolean[] | FormulaError {
  const out: boolean[] = [];
  for (const value of values(args)) {
    const truth = requireBoolean(value);
    if (isFormulaError(truth)) {
      return truth;
    }
    out.push(truth);
  }
  return out;
}

function logical(spec: Omit<FunctionSpec, 'evaluate'>, apply: (truths: boolean[]) => boolean): FunctionSpec {
  return {
    ...spec,
    evaluate: (args) => {
      const parsed = booleans(args);
      return isFormulaError(parsed) ? parsed : apply(parsed);
    },
  };
}

/**
 * MIN/MAX: the extreme of numbers, or of dates — all of one kind. `pick`
 * says whether a value replaces the best so far, given how it compares.
 */
function extreme(spec: Omit<FunctionSpec, 'evaluate'>, pick: (cmp: number) => boolean): FunctionSpec {
  return {
    ...spec,
    evaluate: (args) => {
      const [first, ...rest] = values(args);
      let best: EvalValue = first;
      for (const value of rest) {
        if (best === BLANK || value === BLANK) {
          return BLANK;
        }
        const cmp = compareValues(value, best);
        if (isFormulaError(cmp)) {
          return cmp;
        }
        if (pick(cmp)) {
          best = value;
        }
      }
      if (typeof best === 'string' || typeof best === 'boolean') {
        return new FormulaError('#VALUE!', `${spec.name} takes numbers or dates`);
      }
      return best;
    },
  };
}

const LIST_EXAMPLE = 'e.g. [Metrics.Transactions]';
const MEMBER_EXAMPLE = 'e.g. [Transactions.Distributions]';

/** The one-letter flags CONTAINS's options argument may carry. */
const CONTAINS_OPTIONS = new Set(['i']);

/** KPI([kpi], [period], [scenario]): a KPI ref, then tokens its config supports. */
function checkKpiArgs(args: AstNode[], ctx: CheckCallContext): void {
  const [name, period, section] = args;
  const at = ctx.nextField();
  if (name?.kind !== 'field') {
    throw new FormulaTypeError('The first argument of KPI must be a KPI name, e.g. [Revenue]', '', at);
  }
  const type = ctx.lookup(name.ref);
  if (!type) {
    throw new FormulaTypeError(`Unknown field "[${name.ref}]"`, name.ref, at);
  }
  const label = type.label ?? name.ref;
  if (type.kind !== 'kpi') {
    throw new FormulaTypeError(
      `"[${label}]" is not a KPI — the first argument of KPI must be a KPI name, e.g. [Revenue]`,
      name.ref,
      at
    );
  }
  const atPeriod = ctx.nextField();
  if (period?.kind !== 'field') {
    throw new FormulaTypeError(
      'The second argument of KPI must be a period — [Month], [Quarter] or [Year]',
      '',
      atPeriod
    );
  }
  const periodKey = canonicalKpiPeriod(period.ref);
  if (!periodKey) {
    throw new FormulaTypeError(
      `"[${period.ref}]" is not a period — the second argument of KPI must be [Month], [Quarter] or [Year]`,
      period.ref,
      atPeriod
    );
  }
  if (!type.periods.includes(periodKey)) {
    throw new FormulaTypeError(
      `"[${label}]" has no ${periodKey} values — its periods are ${type.periods.join(', ')}`,
      period.ref,
      atPeriod
    );
  }
  const atSection = ctx.nextField();
  if (section?.kind !== 'field') {
    throw new FormulaTypeError(
      'The third argument of KPI must be a scenario — [Actual] or [Budget]',
      '',
      atSection
    );
  }
  const sectionKey = canonicalKpiSection(section.ref);
  if (!sectionKey) {
    throw new FormulaTypeError(
      `"[${section.ref}]" is not a scenario — the third argument of KPI must be [Actual] or [Budget]`,
      section.ref,
      atSection
    );
  }
  if (!type.sections.includes(sectionKey)) {
    throw new FormulaTypeError(
      `"[${label}]" has no ${kpiSectionLabel(sectionKey)} values — it only has ${type.sections
        .map(kpiSectionLabel)
        .join(' and ')}`,
      section.ref,
      atSection
    );
  }
}

/**
 * KPI([kpi], [period], [scenario]): one KPI value off the row. The name ref is
 * canonical ("kpiData.<id>") and the value lives at
 * "<name ref>.<Period>.<section>.value" — resolveField does the walk, so the
 * engine needs no knowledge of the row shape beyond that join. Invalid
 * period/scenario tokens are #VALUE!, the runtime mirror of typecheck, so a
 * stored formula predating a vocabulary change degrades to a blank cell. A
 * missing value is BLANK, like any blank field.
 */
function evaluateKpi(args: CallArg[], ctx: EvalCallContext): EvalValue | FormulaError {
  const [name, period, section] = args;
  if (name?.kind !== 'ref' || period?.kind !== 'ref' || section?.kind !== 'ref') {
    return new FormulaError('#ERROR!', 'KPI expects KPI([kpi], [period], [scenario])');
  }
  const periodKey = canonicalKpiPeriod(period.ref);
  if (!periodKey) {
    return new FormulaError(
      '#VALUE!',
      `[${period.ref}] is not a KPI period — use [Month], [Quarter] or [Year]`
    );
  }
  const sectionKey = canonicalKpiSection(section.ref);
  if (!sectionKey) {
    return new FormulaError('#VALUE!', `[${section.ref}] is not a KPI scenario — use [Actual] or [Budget]`);
  }
  const value = ctx.resolveField(`${name.ref}.${periodKey}.${sectionKey}.value`);
  if (isFormulaError(value)) {
    return value;
  }
  if (Array.isArray(value)) {
    return new FormulaError('#VALUE!', `Field [${name.ref}] is not a KPI`);
  }
  return ctx.read(value, name.ref);
}

/**
 * DATEADD(date, amount, unit, …): the date moved by each amount of its unit,
 * left to right — DATEADD(d, 1, "y", 5, "m") is d plus a year, then five
 * months. A month or year step keeps the day where the target month has it
 * and clamps to its last day otherwise (Jan 31 + 1 month is Feb 28/29, as
 * Excel's EDATE — Temporal's default). Amounts truncate toward zero and may
 * be negative (there is no DATESUB); units are "day", "month" or "year" in
 * any case, plural, or abbreviated to their first letter. The date is
 * anything DATEVALUE accepts and, like DATEVALUE, a blank date stays blank
 * rather than demanding IFERROR. Errors propagate.
 */
function evaluateDateAdd(args: CallArg[]): EvalValue | FormulaError {
  const [dateValue, ...pairs] = values(args);
  if (dateValue === undefined || pairs.length === 0 || pairs.length % 2 !== 0) {
    // The runtime mirror of the parser's pairing rule.
    return new FormulaError('#ERROR!', 'DATEADD expects DATEADD(date, amount, unit, …)');
  }
  let date = toPlainDate(dateValue);
  if (isFormulaError(date) || date === BLANK) {
    return date;
  }
  for (let i = 0; i < pairs.length; i += 2) {
    const amount = requireNumber(pairs[i]);
    if (isFormulaError(amount)) {
      return amount;
    }
    const unit = parseDateUnit(pairs[i + 1]);
    if (isFormulaError(unit)) {
      return unit;
    }
    try {
      date = date.add({ [`${unit}s`]: Math.trunc(amount) });
    } catch {
      // An amount past what a date can hold (Temporal throws a RangeError).
      return new FormulaError('#NUM!', 'The date is out of range');
    }
  }
  const inRange = isoDateText(date);
  return isFormulaError(inRange) ? inRange : date;
}

/**
 * DATEDIFF(from, to, unit?): to minus from as a signed count of complete
 * units — days exactly; months and years only once the day of the month has
 * come round again (2026-03-15 to 2026-04-14 is 0 months; Jan 31 to Feb 28
 * is 0 too, as Excel's DATEDIF and Temporal both count it). Swapping the
 * dates negates the result, so the sign alone compares two dates. The unit
 * defaults to days. Dates are anything DATEVALUE accepts; a blank on either
 * side keeps the result blank. Errors propagate.
 */
function evaluateDateDiff(args: CallArg[]): EvalValue | FormulaError {
  const [fromValue, toValue_, unitValue] = values(args);
  const from = toPlainDate(fromValue);
  if (isFormulaError(from)) {
    return from;
  }
  const to = toPlainDate(toValue_);
  if (isFormulaError(to)) {
    return to;
  }
  const unit = unitValue === undefined ? 'day' : parseDateUnit(unitValue);
  if (isFormulaError(unit)) {
    return unit;
  }
  if (from === BLANK || to === BLANK) {
    return BLANK;
  }
  // Never -0: a cell showing "-0" for "no complete months yet" would be a puzzle.
  return from.until(to, { largestUnit: unit })[`${unit}s`] || 0;
}

/**
 * CONTAINS(text, search, options?): TRUE when the text contains the search
 * text — Excel's ISNUMBER(FIND(search, text)) in one call, with
 * the text first as in every CONTAINS elsewhere (Tableau, DAX, Salesforce,
 * JS includes). Case-sensitive like FIND unless options holds "i", which
 * folds both sides the way = and the relational operators compare text.
 * Both go through toText, so a blank is "" and a number its digits
 * (CONTAINS(123, 1) is TRUE, as FIND finds it), and an empty search is
 * always contained, as in Excel and JS. Options is a set of one-letter flags
 * in either case; an unknown one is #VALUE! so a typo surfaces rather than
 * silently running case-sensitive. Errors propagate.
 */
function evaluateContains(args: CallArg[]): EvalValue | FormulaError {
  const [text, search, options] = values(args);
  let ignoreCase = false;
  if (options !== undefined) {
    // Flags are case-insensitive, like function names.
    const flags = toText(options).toLowerCase();
    for (const flag of flags) {
      if (!CONTAINS_OPTIONS.has(flag)) {
        return new FormulaError('#VALUE!', `Unknown CONTAINS option "${flag}" — only "i" is supported`);
      }
    }
    ignoreCase = flags.includes('i');
  }
  const haystack = toText(text);
  const needle = toText(search);
  return ignoreCase ? haystack.toLowerCase().includes(needle.toLowerCase()) : haystack.includes(needle);
}

/**
 * INDEX([list], index, [member]): the member's value of the item at the
 * 1-based index (truncated toward zero, like Excel). Out of range — any index
 * into a blank/absent list included — is #REF!, Excel's out-of-range INDEX
 * error; IFERROR catches it. A blank member value stays blank.
 */
function evaluateIndex(args: CallArg[], ctx: EvalCallContext): EvalValue | FormulaError {
  const [list, index, member] = args;
  if (list?.kind !== 'list' || index?.kind !== 'value' || member?.kind !== 'member') {
    return new FormulaError('#ERROR!', 'INDEX expects INDEX([list], index, [field])');
  }
  const position = requireNumber(index.value);
  if (isFormulaError(position)) {
    return position;
  }
  const at = Math.trunc(position);
  if (at < 1 || at > list.items.length) {
    return new FormulaError(
      '#REF!',
      `INDEX ${at} is out of range — [${list.ref}] has ${list.items.length} item(s)`
    );
  }
  return ctx.read(list.items[at - 1]?.[member.key], member.ref);
}

/**
 * SUM([list], [member]): the member's value added up across the items — the
 * REDUCE(0, [list], LAMBDA(acc, acc + IFBLANK([member], 0))) fold without the
 * lambda. Blank/absent lists and blank member values add 0; a text member
 * value is #VALUE!, as in any arithmetic.
 */
function evaluateSum(args: CallArg[], ctx: EvalCallContext): EvalValue | FormulaError {
  const [list, member] = args;
  if (list?.kind !== 'list' || member?.kind !== 'member') {
    return new FormulaError('#ERROR!', 'SUM expects SUM([list], [field])');
  }
  let total = 0;
  for (const item of list.items) {
    const value = requireNumber(ctx.read(item?.[member.key], member.ref));
    if (isFormulaError(value)) {
      return value;
    }
    total += value;
  }
  return finiteOrNumError(total);
}

/**
 * Every function the language ships. The special forms — IF, IFERROR, REDUCE,
 * and the LET/LAMBDA nodes — carry no `evaluate`: the core runs them, since
 * they need laziness, error catching or scope frames.
 */
export const BUILTINS = createRegistry([
  {
    name: 'IF',
    params: [
      { name: 'condition', kind: 'expr', type: 'boolean' },
      { name: 'then', kind: 'expr', type: 'same' },
      { name: 'else', kind: 'expr', type: 'same', optional: true },
    ],
    returns: 'same',
    signature: 'IF(condition, then, else?)',
    doc: 'Picks one of two values based on a condition, e.g. IF([Metrics.IRR] > 0.2, [Metrics.FMV] * 2, [Metrics.FMV]).',
  },
  logical(
    {
      name: 'AND',
      params: [
        { name: 'a', kind: 'expr', type: 'boolean' },
        { name: 'b', kind: 'expr', type: 'boolean', optional: true, rest: true },
      ],
      returns: 'boolean',
      signature: 'AND(a, b, …)',
      doc: 'TRUE when every condition holds.',
    },
    (truths) => truths.every(Boolean)
  ),
  logical(
    {
      name: 'OR',
      params: [
        { name: 'a', kind: 'expr', type: 'boolean' },
        { name: 'b', kind: 'expr', type: 'boolean', optional: true, rest: true },
      ],
      returns: 'boolean',
      signature: 'OR(a, b, …)',
      doc: 'TRUE when any condition holds.',
    },
    (truths) => truths.some(Boolean)
  ),
  logical(
    {
      name: 'NOT',
      params: [{ name: 'value', kind: 'expr', type: 'boolean' }],
      returns: 'boolean',
      signature: 'NOT(value)',
      doc: 'Flips TRUE and FALSE.',
    },
    (truths) => !truths[0]
  ),
  extreme(
    {
      name: 'MIN',
      params: [
        { name: 'a', kind: 'expr', type: 'same' },
        { name: 'b', kind: 'expr', type: 'same', optional: true, rest: true },
      ],
      returns: 'same',
      sameOf: ['number', 'date'],
      signature: 'MIN(a, b, …)',
      doc: 'The smallest of its arguments — numbers, or dates.',
    },
    (cmp) => cmp < 0
  ),
  extreme(
    {
      name: 'MAX',
      params: [
        { name: 'a', kind: 'expr', type: 'same' },
        { name: 'b', kind: 'expr', type: 'same', optional: true, rest: true },
      ],
      returns: 'same',
      sameOf: ['number', 'date'],
      signature: 'MAX(a, b, …)',
      doc: 'The largest of its arguments — numbers, or dates.',
    },
    (cmp) => cmp > 0
  ),
  numeric(
    {
      name: 'ROUND',
      params: [
        { name: 'value', kind: 'expr', type: 'number' },
        { name: 'digits', kind: 'expr', type: 'number', optional: true },
      ],
      returns: 'number',
      signature: 'ROUND(value, digits?)',
      doc: 'Rounds half away from zero; negative digits round to tens, hundreds, …',
    },
    (numbers) => round(numbers[0], numbers[1] ?? 0)
  ),
  numeric(
    {
      name: 'ABS',
      params: [{ name: 'value', kind: 'expr', type: 'number' }],
      returns: 'number',
      signature: 'ABS(value)',
      doc: 'The absolute value.',
    },
    (numbers) => Math.abs(numbers[0])
  ),
  {
    name: 'IFERROR',
    params: [
      { name: 'value', kind: 'expr', type: 'same' },
      { name: 'fallback', kind: 'expr', type: 'same' },
    ],
    returns: 'same',
    signature: 'IFERROR(value, fallback)',
    doc: 'Replaces an error value (e.g. #DIV/0!) with a fallback. A blank is not an error: pair with IFBLANK to cover both.',
  },
  {
    name: 'IFBLANK',
    params: [
      { name: 'value', kind: 'expr', type: 'same' },
      { name: 'fallback', kind: 'expr', type: 'same' },
    ],
    returns: 'same',
    signature: 'IFBLANK(value, fallback)',
    doc: 'The value, or the fallback where it is blank — the way to count a missing number as 0, e.g. IFBLANK([Metrics.Distributions], 0) + [Metrics.FMV]. Elsewhere a blank makes the calculation around it blank.',
  },
  {
    name: 'ISBLANK',
    params: [{ name: 'value', kind: 'expr', type: 'any' }],
    returns: 'boolean',
    blanks: 'handle',
    signature: 'ISBLANK(value)',
    doc: 'TRUE when there is no value there — a missing field, empty text, or a number that is not a number, e.g. IF(ISBLANK([Company.Sector]), "Unassigned", [Company.Sector]).',
    // Every shape a missing value arrives in is already BLANK by the time it is read (see
    // toValue), so this is that one test, plus the empty string a literal or a concatenation
    // can still produce. No trimming, so ISBLANK(" ") is FALSE — Excel's answer too.
    evaluate: (args) => {
      const [value] = values(args);
      return value === BLANK || value === '';
    },
  },
  {
    name: 'CONTAINS',
    params: [
      { name: 'text', kind: 'expr', type: 'string' },
      { name: 'search', kind: 'expr', type: 'string' },
      { name: 'options', kind: 'expr', type: 'string', optional: true },
    ],
    returns: 'boolean',
    signature: 'CONTAINS(text, search, options?)',
    doc: 'TRUE when the text contains the search text, e.g. CONTAINS([Round.Security], "Preferred"). Case-sensitive unless options is "i": CONTAINS([Round.Security], "preferred", "i").',
    evaluate: evaluateContains,
  },
  {
    name: 'CONCAT',
    params: [
      { name: 'a', kind: 'expr', type: 'any' },
      { name: 'b', kind: 'expr', type: 'any', optional: true, rest: true },
    ],
    returns: 'string',
    // Text-building reads a blank as "": the one place a blank is not propagated as data.
    blanks: 'handle',
    signature: 'CONCAT(a, b, …)',
    doc: 'Joins values as text — what & does: "Fund " & [Company.Fund] is CONCAT("Fund ", [Company.Fund]). Numbers, dates and TRUE/FALSE render as TEXT() renders them; a blank is empty text.',
    evaluate: (args) => values(args).map(toText).join(''),
  },
  {
    name: 'VALUE',
    params: [{ name: 'text', kind: 'expr', type: 'string' }],
    returns: 'number',
    signature: 'VALUE(text)',
    doc: 'The number a piece of text spells, e.g. VALUE("12.5"). Anything that is not entirely a number is #VALUE!; a blank stays blank.',
    evaluate: (args) => {
      const [text] = values(args);
      return typeof text === 'string' ? parseNumber(text) : requireNumber(text);
    },
  },
  {
    name: 'TEXT',
    params: [{ name: 'value', kind: 'expr', type: 'any' }],
    returns: 'string',
    signature: 'TEXT(value)',
    doc: 'A value as text — a number\'s digits, a date as YYYY-MM-DD, TRUE or FALSE — e.g. "Held " & TEXT([Metrics.Investment Age]) & " years". A blank stays blank.',
    evaluate: (args) => toText(values(args)[0]),
  },
  {
    name: 'BLANK',
    params: [],
    // A blank has whatever type its place needs: it unifies with anything.
    returns: 'same',
    signature: 'BLANK()',
    doc: 'An empty value, for leaving a cell blank, e.g. IF(ISBLANK([Metrics.FMV]), BLANK(), [Metrics.FMV] * 2). Like any empty cell it makes arithmetic around it blank and reads as "" under &.',
    // The sentinel itself, so an author can produce what a missing cell produces.
    evaluate: () => BLANK,
  },
  {
    name: 'REDUCE',
    params: [
      { name: 'initial', kind: 'expr', type: 'same' },
      {
        name: 'list',
        kind: 'list',
        expect: `The second argument of REDUCE must be a list field, ${LIST_EXAMPLE}`,
      },
      { name: 'lambda', kind: 'lambda', type: 'same' },
    ],
    returns: 'same',
    signature: 'REDUCE(initial, [list], LAMBDA(acc, expression))',
    doc: 'Walks a list, carrying a running value through each item — the LAMBDA runs once per item and its result becomes the next running value.',
  },
  {
    name: 'LAMBDA',
    params: [
      { name: 'acc', kind: 'expr' },
      { name: 'expression', kind: 'expr', type: 'same' },
    ],
    returns: 'same',
    signature: 'LAMBDA(acc, expression)',
    doc: 'The step REDUCE runs for each item of the list.',
  },
  {
    name: 'LET',
    // The params state the arity the editor shows (three or more); the parser reads LET
    // with its own alternating name/value grammar (parseLet) and never consults them.
    params: [
      { name: 'name', kind: 'expr' },
      { name: 'value', kind: 'expr', rest: true },
      { name: 'calculation', kind: 'expr', type: 'same' },
    ],
    returns: 'same',
    signature: 'LET(name, value, …, calculation)',
    doc: 'Names a value to reuse it, e.g. LET(net, [Metrics.FMV] - [Metrics.Cost], IF(net > 0, net / [Metrics.Cost], 0)). Names and values alternate; the last argument is the calculation, and each value may use the names before it.',
  },
  {
    name: 'SIZE',
    params: [
      { name: 'list', kind: 'list', expect: `The argument of SIZE must be a list field, ${LIST_EXAMPLE}` },
    ],
    returns: 'number',
    signature: 'SIZE([list])',
    doc: 'The number of items in a list, e.g. SIZE([Metrics.Transactions]).',
    // A blank/absent list has 0 items.
    evaluate: (args) =>
      args[0]?.kind === 'list'
        ? args[0].items.length
        : new FormulaError('#ERROR!', 'SIZE expects SIZE([list])'),
  },
  {
    name: 'INDEX',
    params: [
      {
        name: 'list',
        kind: 'list',
        expect: `The first argument of INDEX must be a list field, ${LIST_EXAMPLE}`,
      },
      { name: 'index', kind: 'expr', type: 'number' },
      {
        name: 'field',
        kind: 'member',
        type: 'same',
        expect: `The third argument of INDEX must be a field of the list, ${MEMBER_EXAMPLE}`,
        // Excel's 2-argument INDEX: items here are records, so the field must be named.
        missing:
          'INDEX needs a third argument picking which field of the item, e.g. INDEX([Metrics.Transactions], 2, [Transactions.Distributions])',
      },
    ],
    returns: 'same',
    signature: 'INDEX([list], index, [field])',
    doc: 'One field of one item of a list, by position (1-based). Pair with SIZE for the last item: INDEX([Metrics.Transactions], SIZE([Metrics.Transactions]), [Transactions.Distributions]).',
    evaluate: evaluateIndex,
  },
  {
    name: 'SUM',
    params: [
      {
        name: 'list',
        kind: 'list',
        expect: `The first argument of SUM must be a list field, e.g. SUM([Metrics.Transactions], [Transactions.Distributions]). To add plain numbers, use + instead`,
        hint: 'To add plain numbers, use + instead',
      },
      {
        name: 'field',
        kind: 'member',
        numeric: true,
        expect: `The second argument of SUM must be a field of the list, ${MEMBER_EXAMPLE}`,
      },
    ],
    returns: 'number',
    signature: 'SUM([list], [field])',
    doc: 'Adds up one numeric field across the items of a list, blank values counting as 0, e.g. SUM([Metrics.Transactions], [Transactions.Distributions]).',
    evaluate: evaluateSum,
  },
  {
    name: 'KPI',
    params: [
      {
        name: 'kpi',
        kind: 'kpi',
        expect: 'The first argument of KPI must be a KPI name, e.g. KPI([Revenue], [Quarter], [Actual])',
      },
      {
        name: 'period',
        kind: 'token',
        values: KPI_PERIOD_KEYS,
        expect: 'The second argument of KPI must be a period — [Month], [Quarter] or [Year]',
      },
      {
        name: 'scenario',
        kind: 'token',
        values: KPI_SECTION_KEYS,
        expect: 'The third argument of KPI must be a scenario — [Actual] or [Budget]',
      },
    ],
    returns: 'number',
    signature: 'KPI([kpi], [period], [scenario])',
    doc: "One KPI value, e.g. KPI([Revenue], [Quarter], [Actual]) — the KPI's quarterly Actual for the date the row's data is as of. The scenario is [Actual] (reported) or [Budget].",
    check: checkKpiArgs,
    evaluate: evaluateKpi,
  },
  {
    name: 'TODAY',
    params: [],
    returns: 'date',
    signature: 'TODAY()',
    doc: 'Today\'s date, e.g. IF([Metrics.Exit Date] > TODAY(), "future", "past").',
    evaluate: () => today(),
  },
  {
    name: 'DATEVALUE',
    params: [{ name: 'value', kind: 'expr', type: 'any' }],
    returns: 'date',
    signature: 'DATEVALUE(value)',
    doc: 'The date an ISO date/datetime text or an epoch-milliseconds number names, e.g. DATEVALUE("2026-03-15"); a blank stays blank.',
    evaluate: (args) => toPlainDate(values(args)[0]),
  },
  {
    name: 'ASOFDATE',
    params: [],
    returns: 'date',
    signature: 'ASOFDATE()',
    doc: "The page's As Of Date — the date the data is viewed as of; usually what you want over TODAY(). Today where no As Of Date applies.",
    evaluate: (_, ctx) => {
      if (ctx.asOfDate === undefined) {
        return today();
      }
      try {
        return Temporal.PlainDate.from(ctx.asOfDate);
      } catch {
        return new FormulaError('#VALUE!', `"${ctx.asOfDate}" is not a date`);
      }
    },
  },
  {
    name: 'DATEADD',
    params: [
      { name: 'date', kind: 'expr', type: 'date' },
      { name: 'amount', kind: 'expr', type: 'number', rest: true },
      { name: 'unit', kind: 'expr', type: 'string', rest: true },
    ],
    returns: 'date',
    signature: 'DATEADD(date, amount, unit, …)',
    doc: 'A date moved by an amount of "day", "month" or "year" units (or "d", "m", "y"); negative amounts go back, e.g. DATEADD([Metrics.Initial Investment Date], 5, "year"). Amount and unit may repeat: DATEADD([Round.Investment Date], 1, "y", 5, "m"). Month steps keep the day where the month allows, else its last day. A blank date stays blank.',
    evaluate: evaluateDateAdd,
  },
  {
    name: 'DATEDIFF',
    params: [
      { name: 'from', kind: 'expr', type: 'date' },
      { name: 'to', kind: 'expr', type: 'date' },
      { name: 'unit', kind: 'expr', type: 'string', optional: true },
    ],
    returns: 'number',
    signature: 'DATEDIFF(from, to, unit?)',
    doc: 'The number of complete units from one date to the next — "day" (the default), "month" or "year" — negative when the second date is earlier, e.g. DATEDIFF([Metrics.Initial Investment Date], ASOFDATE(), "year") for the years held. A blank date stays blank.',
    evaluate: evaluateDateDiff,
  },
]);
